/**
 * § G27 — change where one attendee flies FROM, and which day they fly.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 * The roster could add an attendee and delete an attendee, and nothing in
 * between. Two real cases from Wade's own meeting, both unfixable without it:
 *
 *   "I only fly AA and the earliest flight gets in too late"  → a DATE change:
 *   fly in the day BEFORE. The seeded dates put everyone on the meeting day.
 *
 *   "Alex only flies Southwest, who fly from DAL"             → an ORIGIN
 *   change, which nothing on the surface could express.
 *
 * ── Two things this form is careful about ──────────────────────────────────
 * It states the CONSEQUENCE before the click, not after. Changing either field
 * discards the fares already found — they describe a journey nobody is taking
 * any more — and an organizer who is not told that will read the resulting
 * empty board as the feature breaking.
 *
 * And it opens PRE-FILLED with what is true now, so the day-before case is one
 * keystroke on a date that is already there, not a date the organizer has to
 * work out from the meeting time.
 */
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { travelGroupApi, type RosterMember, type TravelMode } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** The day before an ISO date, or null when there is no date to step back from. */
export function dayBefore(isoDate: string | null | undefined): string | null {
  if (!isoDate) return null;
  const d = new Date(`${isoDate}T12:00:00Z`); // midday: never trips over a DST edge
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

export default function AttendeeItineraryEditor({
  groupId,
  member,
  onClose,
}: {
  groupId: string;
  member: RosterMember;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const outbound = member.legs.find((l) => l.leg_role === 'outbound');
  const ret = member.legs.find((l) => l.leg_role === 'return');

  const [origin, setOrigin] = useState((outbound?.origin || member.home_airport || '').toUpperCase());
  const [alts, setAlts] = useState((member.candidate_origins ?? []).join(', '));
  const [mode, setMode] = useState<TravelMode>(member.travel_mode ?? 'flight');
  const [modeNote, setModeNote] = useState(member.travel_mode_note ?? '');
  const [outDate, setOutDate] = useState(outbound?.departure_date ?? '');
  const [backDate, setBackDate] = useState(ret?.departure_date ?? '');
  const [error, setError] = useState('');

  const mut = useMutation({
    mutationFn: () =>
      travelGroupApi.editAttendeeItinerary(groupId, member.membership_id, {
        travel_mode: mode,
        travel_mode_note: modeNote.trim() || null,
        home_airport: origin.trim().toUpperCase(),
        alternate_airports: alts
          .split(/[,/\s]+/)
          .map((a) => a.trim().toUpperCase())
          .filter(Boolean),
        ...(outDate ? { outbound_date: outDate } : {}),
        ...(backDate ? { return_date: backDate } : {}),
      }),
    onSuccess: () => {
      // The roster AND the trip page both render this itinerary; refreshing one
      // leaves the other arguing with it.
      qc.invalidateQueries({ queryKey: ['roster', groupId] });
      qc.invalidateQueries({ queryKey: ['trip', member.trip_id] });
      onClose();
    },
    onError: (e: any) => setError(e?.response?.data?.error || e.message),
  });

  const earlier = dayBefore(outDate);

  return (
    <div className="mt-1 flex flex-col gap-2 rounded-md border border-border bg-muted/30 p-3">
      {/*
        § G33 — the mode leads, because it decides whether ANY of the rest of
        this form applies. Somebody driving in has no airport and no dates, and
        asking for them first would have the organizer fill in fields that are
        about to be discarded.
      */}
      <label className="flex flex-col gap-1">
        <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Getting there by</span>
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as TravelMode)}
          className="h-8 w-44 rounded border border-border bg-background px-1.5 text-xs"
        >
          <option value="flight">Flight</option>
          <option value="car">Car — driving</option>
          <option value="train">Train</option>
          <option value="bus">Bus</option>
          <option value="other">Other arrangements</option>
        </select>
      </label>

      {mode !== 'flight' && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Note (optional)</span>
          <Input
            value={modeNote}
            onChange={(e) => setModeNote(e.target.value)}
            placeholder="driving, ~4h"
            className="h-8 w-64 text-xs"
          />
        </label>
      )}

      {mode !== 'flight' && (
        <p className="text-[11px] text-muted-foreground">
          {member.display_name} will not be shopped for flights, and the roster and manifest will
          say how they are arriving instead of showing an empty flight row.
        </p>
      )}

      {mode === 'flight' && (
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Flies from</span>
          <Input
            value={origin}
            onChange={(e) => setOrigin(e.target.value)}
            placeholder="DAL"
            className="h-8 w-24 font-mono text-xs uppercase"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Or also</span>
          <Input
            value={alts}
            onChange={(e) => setAlts(e.target.value)}
            placeholder="DFW, AUS"
            className="h-8 w-32 font-mono text-xs uppercase"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Out</span>
          <Input
            type="date"
            value={outDate}
            onChange={(e) => setOutDate(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Back</span>
          <Input
            type="date"
            value={backDate}
            onChange={(e) => setBackDate(e.target.value)}
            className="h-8 w-36 text-xs"
          />
        </label>
      </div>
      )}

      {/*
        The whole point of the date field, made one click instead of arithmetic.
        "The earliest flight gets in too late" is answered by flying in the night
        before, and an organizer should not have to compute that date themselves.
      */}
      {mode === 'flight' && earlier && earlier !== outDate && (
        <button
          type="button"
          onClick={() => setOutDate(earlier)}
          className="self-start rounded-full border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted"
        >
          Fly in the day before ({earlier})
        </button>
      )}

      {/* Stated BEFORE the click. An empty board after an edit reads as a bug
          unless the person was told the old fares no longer apply. */}
      {mode === 'flight' && (
      <p className="text-[11px] text-muted-foreground">
        Changing the airport or the dates discards the fares already found for {member.display_name} —
        they price a different journey — and starts a fresh search.
      </p>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="flex items-center gap-2">
        <Button size="sm" className="h-7 px-3 text-xs" disabled={mut.isPending} onClick={() => mut.mutate()}>
          {mut.isPending ? 'Saving…' : 'Save and reshop'}
        </Button>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-muted-foreground hover:text-foreground"
          disabled={mut.isPending}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
